import { useState } from 'react';
import {
  useLinkPracticeMember,
  usePracticeMembers,
  useUnlinkPracticeMember,
} from '../lib/queries';
import type { PracticeMemberRole } from '../lib/types';
import Spinner from './Spinner';

const ROLES: { value: PracticeMemberRole; label: string }[] = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'DOCTOR', label: 'Pediatra' },
  { value: 'STAFF', label: 'Equipe' },
];

export default function PracticeMembers({
  practiceId,
  practiceSlug,
}: {
  practiceId: string;
  practiceSlug: string;
}) {
  const { data, isLoading, error } = usePracticeMembers(practiceId);
  const link = useLinkPracticeMember();
  const unlink = useUnlinkPracticeMember();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PracticeMemberRole>('DOCTOR');

  async function onLink(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await link.mutateAsync({ practiceId, email, role });
      setEmail('');
    } catch {
      /* mutation error shown below */
    }
  }

  const members = data ?? [];
  const linkErr = link.error as { message?: string; details?: string; hint?: string } | null;

  return (
    <div className="card shadow-sm mt-3">
      <div className="card-body">
        <h2 className="h6 text-muted mb-1">Acesso ao painel pediatra</h2>
        <p className="small text-muted mb-3">
          Usuários vinculados entram em{' '}
          <code>app.bebecare</code> / consultório <code>{practiceSlug}</code>. O email
          precisa já existir no Supabase Auth (magic link uma vez).
        </p>

        {isLoading && <Spinner />}
        {error && (
          <div className="alert alert-danger small py-2">
            {(error as Error).message}
          </div>
        )}

        {!isLoading && members.length > 0 && (
          <ul className="list-group list-group-flush mb-3">
            {members.map((m) => (
              <li
                key={m.id}
                className="list-group-item d-flex align-items-center gap-2 px-0"
              >
                <div className="flex-grow-1 min-width-0">
                  <div className="fw-semibold small text-truncate">{m.email}</div>
                  <span className="badge text-bg-light text-dark border">
                    {m.role}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  disabled={unlink.isPending}
                  onClick={() => {
                    if (
                      !window.confirm(
                        `Remover acesso de ${m.email} a este consultório?`,
                      )
                    ) {
                      return;
                    }
                    void unlink.mutateAsync({
                      memberId: m.id,
                      practiceId,
                    });
                  }}
                >
                  Remover
                </button>
              </li>
            ))}
          </ul>
        )}

        {!isLoading && members.length === 0 && (
          <p className="small text-muted mb-3">Nenhum usuário vinculado.</p>
        )}

        <form onSubmit={onLink} className="border-top pt-3">
          <label className="form-label small fw-semibold">Vincular por email</label>
          <div className="row g-2">
            <div className="col-md-6">
              <input
                type="email"
                className="form-control form-control-sm"
                placeholder="pediatra@consultorio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select form-select-sm"
                value={role}
                onChange={(e) => setRole(e.target.value as PracticeMemberRole)}
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3 d-grid">
              <button
                type="submit"
                className="btn btn-sm btn-bb"
                disabled={link.isPending}
              >
                {link.isPending ? 'Vinculando…' : 'Vincular'}
              </button>
            </div>
          </div>
          {linkErr && (
            <div className="alert alert-danger small mt-2 mb-0 py-2">
              {linkErr.message?.includes('user_not_found')
                ? 'Usuário não encontrado. Peça para fazer login uma vez (magic link) no app web ou admin.'
                : linkErr.hint ?? linkErr.message ?? 'Erro ao vincular'}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
